/* global bootstrap */

// Bootstrap tooltips are opt-in: every trigger needs an instance built in JS.
// Server-rendered markup could be handled once on load, but Vue re-renders and
// AJAX-injected HTML add, remove and retitle triggers at any time, so a document-wide
// MutationObserver owns the lifecycle instead of each component initializing (and
// forgetting to re-initialize) its own tooltips.

// `data-bs-toggle="tooltip"` is the Bootstrap convention. `data-tooltip` is our own
// opt-in for elements whose `data-bs-toggle` is already spoken for by another plugin
// (a collapse or dropdown trigger that also wants a tooltip); either way the text comes
// from `title` or `data-bs-title`.
const SELECTOR = '[data-bs-toggle="tooltip"], [data-tooltip]';

const TOGGLE_ATTRIBUTES = ['data-bs-toggle', 'data-tooltip'];

// Server-rendered tooltips use `title`, which Bootstrap reads once and then moves to
// `data-bs-original-title`. Vue templates use `data-bs-title` instead, since a reactive
// `title` would keep reappearing after Bootstrap stripped it — and observing `title`
// would loop, because disposing an instance puts it back.
const CONTENT_ATTRIBUTE = 'data-bs-title';

const elementsIn = (node) => {
  if (!(node instanceof Element)) return [];

  const self = node.matches(SELECTOR) ? [node] : [];
  return self.concat([...node.querySelectorAll(SELECTOR)]);
};

const create = (element) => bootstrap.Tooltip.getOrCreateInstance(element);

const destroy = (element) => {
  const instance = bootstrap.Tooltip.getInstance(element);
  if (instance) instance.dispose();
};

// Bootstrap freezes the tooltip options when the instance is built, so a new
// `data-bs-title` has to be pushed in. setContent also re-renders a tooltip that is on
// screen right now, which matters for the triggers whose text flips as you click them.
const retitle = (element) => {
  const instance = bootstrap.Tooltip.getInstance(element);
  const title = element.getAttribute(CONTENT_ATTRIBUTE) || '';

  if (!instance) {
    create(element);
  } else if (title === '') {
    // Nothing left to say, and Bootstrap would still draw the empty frame.
    destroy(element);
  } else {
    instance.setContent({ '.tooltip-inner': title });
  }
};

// Builds the tooltips inside `root` (a document, element or detached fragment).
export function initTooltips(root = document) {
  elementsIn(root === document ? document.documentElement : root).forEach(create);
}

// Keeps tooltips in sync with the DOM for as long as the page lives. Returns the
// observer so callers can stop watching.
export function watchTooltips(target = document.body) {
  const touched = new Set();
  const retitled = new Set();
  let scheduled = false;

  // One pass per element, whatever mix of mutations it was caught up in.
  const sync = (element) => {
    if (!element.isConnected || !element.matches(SELECTOR)) {
      destroy(element);
    } else if (retitled.has(element)) {
      retitle(element);
    } else {
      create(element);
    }
  };

  const flush = () => {
    scheduled = false;
    const elements = new Set([...touched, ...retitled]);
    touched.clear();
    retitled.clear();
    elements.forEach(sync);
  };

  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      if (mutation.type === 'attributes') {
        (mutation.attributeName === CONTENT_ATTRIBUTE ? retitled : touched).add(mutation.target);
        return;
      }

      mutation.addedNodes.forEach((node) => elementsIn(node).forEach((el) => touched.add(el)));
      mutation.removedNodes.forEach((node) => elementsIn(node).forEach((el) => touched.add(el)));
    });

    if (touched.size === 0 && retitled.size === 0) return;

    // Batch a whole render pass into one sweep: a re-rendered list otherwise visits the
    // same tooltips once per mutation record.
    if (!scheduled) {
      scheduled = true;
      requestAnimationFrame(flush);
    }
  });

  observer.observe(target, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: [...TOGGLE_ATTRIBUTES, CONTENT_ATTRIBUTE],
  });

  return observer;
}
